// @givmo/design-system — OtpField (ports Flutter `CodeTextField`, text_fields/code_text_field.dart).
//
// The Flutter widget is a 6-digit code entry built on Pinput: each cell is 72×48 (on the web the
// cells share the row's width instead, whatever the host gives it, so a phone fits), white fill,
// radius all12, text style `h2TextStyle` (Lato w700 s34), with the SAME two-state border as the
// text field — neutral-500 idle, blue when the cell is active/focused, magenta on error — plus the
// focus-glow shadow (rgba(81,149,209,0.2)) on the active cell. (Flutter's idle gray60 is 2.65:1 on
// white, under SC 1.4.11's 3:1 for a field's boundary; EP-WEB45 moved the web to the house one.)
//
// Built on Base UI's OTP Field (@base-ui/react/otp-field) — a purpose-built segmented OTP
// primitive (composite focus management, paste handling, numeric validation, autofill). This
// keeps the port dependency-light: NO extra input-OTP package is added; OTP support already
// ships inside the existing `@base-ui/react` dependency.
//
// a11y: each slot is a real `<input inputMode="numeric" autoComplete="one-time-code">` so OS/
// browser one-time-code autofill works, and the slots are wired into a composite for arrow-key
// navigation. `aria-invalid` is set on each slot in the error state. `testId` forwards to the
// root; per-slot testids derive as `${testId}-N`.
import { OTPField as BaseOtpField } from '@base-ui/react/otp-field';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

export interface OtpFieldProps extends TestIdProps {
  /** Number of digit cells. Defaults to 6 (Flutter CodeTextField length). */
  length?: number;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Fired when all cells are filled. */
  onComplete?: (value: string) => void;
  disabled?: boolean;
  /** Error state — switches the cell borders to magenta. */
  error?: boolean;
  /** Form field name (for native form submission). */
  name?: string;
  className?: string;
  'aria-label'?: string;
}

export function OtpField({
  length = 6,
  value,
  defaultValue,
  onValueChange,
  onComplete,
  disabled,
  error = false,
  name,
  className,
  testId,
  'aria-label': ariaLabel,
}: OtpFieldProps) {
  return (
    <BaseOtpField.Root
      length={length}
      value={value}
      defaultValue={defaultValue}
      onValueChange={(next) => onValueChange?.(next)}
      onValueComplete={(next) => onComplete?.(next)}
      disabled={disabled}
      name={name}
      aria-label={ariaLabel}
      className={cn('flex w-full items-center justify-between gap-8', className)}
      {...testIdProp(testId)}
    >
      {Array.from({ length }).map((_, i) => (
        <BaseOtpField.Input
          // Slots are positional; index keys are correct here.
          key={i}
          aria-invalid={error || undefined}
          className={cn(
            // 48 tall, white fill, radius all12, Lato w700 s34, centered digit. The WIDTH is the row's,
            // shared equally from zero (AS-WEB31): a fixed cell width never showed in a host of definite
            // width, yet it set the row's smallest width to six such cells, so a phone's card could not
            // be narrower and the employer portal's code prompt ran 66px off a 390 screen.
            'h-[48px] w-0 min-w-0 flex-1 rounded-all12 border bg-white text-center',
            'font-sans text-s34 font-w700 tracking-givmo text-black caret-black',
            'outline-none transition-shadow',
            // Two-state border (recon §1 / code_text_field.dart): idle neutral-500, focus blue + glow.
            // The idle colour is the house control boundary (4.68:1 on white, held by the token
            // floor), not Flutter's gray60 (2.65:1).
            'border-neutral-500',
            'focus:border-blue focus:shadow-focus-glow',
            // Error: magenta in both idle and focused states.
            error && 'border-magenta focus:border-magenta',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          {...testIdProp(testId ? `${testId}-${i + 1}` : undefined)}
        />
      ))}
    </BaseOtpField.Root>
  );
}
